import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

interface CoreCutDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: string;
}

export function CoreCutDialog({ open, onOpenChange, orgId }: CoreCutDialogProps) {
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    property_name: "",
    location_on_roof: "",
    deck_type: "",
    deck_thickness: "",
    fastener_type: "",
    fastener_pattern: "",
    insulation_type: "",
    insulation_thickness: "",
    condition_notes: "",
  });

  const handleSave = async () => {
    if (!formData.property_name || !formData.deck_type) {
      toast.error("Please fill in required fields: Property Name and Deck Type");
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase.from("core_cuts").insert({
        org_id: orgId,
        property_name: formData.property_name,
        location_on_roof: formData.location_on_roof || null,
        deck_type: formData.deck_type,
        deck_thickness: formData.deck_thickness || null,
        fastener_type: formData.fastener_type || null,
        fastener_pattern: formData.fastener_pattern || null,
        insulation_type: formData.insulation_type || null,
        insulation_thickness: formData.insulation_thickness || null,
        membrane_layers: [],
        condition_notes: formData.condition_notes || null,
        photos: [],
      });

      if (error) throw error;

      toast.success("Core cut results saved successfully!");
      onOpenChange(false);
      
      // Reset form
      setFormData({
        property_name: "",
        location_on_roof: "",
        deck_type: "",
        deck_thickness: "",
        fastener_type: "",
        fastener_pattern: "",
        insulation_type: "",
        insulation_thickness: "",
        condition_notes: "",
      });
    } catch (error: any) {
      console.error("Error saving core cut:", error);
      toast.error("Failed to save core cut: " + error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Log Core Cut Results</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="property_name">Property Name *</Label>
            <Input
              id="property_name"
              value={formData.property_name}
              onChange={(e) => setFormData({ ...formData, property_name: e.target.value })}
              placeholder="e.g., Warehouse 4B"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="location_on_roof">Location on Roof</Label>
            <Input
              id="location_on_roof"
              value={formData.location_on_roof}
              onChange={(e) => setFormData({ ...formData, location_on_roof: e.target.value })}
              placeholder="e.g., North wing, near HVAC unit"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="deck_type">Deck Type *</Label>
              <Select
                value={formData.deck_type}
                onValueChange={(value) => setFormData({ ...formData, deck_type: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select deck type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Plywood">Plywood</SelectItem>
                  <SelectItem value="OSB">OSB</SelectItem>
                  <SelectItem value="Concrete">Concrete</SelectItem>
                  <SelectItem value="Metal">Metal</SelectItem>
                  <SelectItem value="Gypsum">Gypsum</SelectItem>
                  <SelectItem value="Wood Planks">Wood Planks</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="deck_thickness">Deck Thickness</Label>
              <Input
                id="deck_thickness"
                value={formData.deck_thickness}
                onChange={(e) => setFormData({ ...formData, deck_thickness: e.target.value })}
                placeholder="e.g., 5/8 inch"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="fastener_type">Fastener Type</Label>
              <Input
                id="fastener_type"
                value={formData.fastener_type}
                onChange={(e) => setFormData({ ...formData, fastener_type: e.target.value })}
                placeholder="e.g., 3-inch screws"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="fastener_pattern">Fastener Pattern</Label>
              <Input
                id="fastener_pattern"
                value={formData.fastener_pattern}
                onChange={(e) => setFormData({ ...formData, fastener_pattern: e.target.value })}
                placeholder="e.g., 6 in OC field, 4 in OC perimeter"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="insulation_type">Insulation Type</Label>
              <Input
                id="insulation_type"
                value={formData.insulation_type}
                onChange={(e) => setFormData({ ...formData, insulation_type: e.target.value })}
                placeholder="e.g., Polyiso, XPS"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="insulation_thickness">Insulation Thickness</Label>
              <Input
                id="insulation_thickness"
                value={formData.insulation_thickness}
                onChange={(e) => setFormData({ ...formData, insulation_thickness: e.target.value })}
                placeholder="e.g., 2 inches"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="condition_notes">Condition Notes</Label>
            <Textarea
              id="condition_notes"
              value={formData.condition_notes}
              onChange={(e) => setFormData({ ...formData, condition_notes: e.target.value })}
              placeholder="Document any moisture, damage, or concerns found..."
              rows={4}
            />
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Save Core Cut
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}